/**
 * ป้ายอุปกรณ์ใน dropdown ของฟอร์มการเช่า (สร้าง/แก้ไข) — ที่เดียว ใช้ทั้งสองหน้า
 *
 * R3U-06 (รอบพิสูจน์ที่ 3, 8 ก.ย. 2026): ฟอร์มแก้ไขเคยประกอบป้ายด้วย template literal
 * `${code} - S/N: ${serial_number} - ${model}` ตรง ๆ → ของนับจำนวน (ไม่มี S/N) ขึ้น
 * "DEV-SAMPLE-STOCK-01 - S/N: null - ไม่ระบุรุ่น" (คำว่า "null" โผล่บนจอ) · หน้าสร้างมีสาขาแยกให้
 * ของนับจำนวนอยู่แล้วแต่หน้าแก้ไขไม่มี → รวมเป็น helper เดียวเพื่อไม่ให้สองหน้าหลุดจากกันอีก
 *
 * กติกา:
 * - ชื่อหลัก = รหัส (fallback S/N แล้ว #id)
 * - ส่วน "S/N: …" แสดง**เฉพาะเมื่อมี S/N จริง** (ไม่ใช่ null/ว่าง) — ของนับจำนวนไม่มีส่วนนี้
 * - `availableQuantity` (ของนับจำนวนหน้าสร้าง) → ต่อท้าย "(คงเหลือ: N หน่วย)"
 * - `openMaintenanceCount` > 0 → ต่อท้ายคำเตือนเคสซ่อมค้าง (P-S475-1) เหมือนเดิม
 */

export interface DeviceLabelSource {
  id?: number;
  code?: string | null;
  serial_number?: string | null;
  is_serialized?: boolean | null;
  model?: string | null;
  device_model?: { name?: string | null } | null;
  unit?: string | null;
}

export interface DeviceLabelOptions {
  /** ยอดที่เหลือให้เลือกได้ (หน้าสร้างคำนวณหักที่รายการอื่นในฟอร์มแล้ว) — ใส่เฉพาะของนับจำนวน */
  availableQuantity?: number;
  openMaintenanceCount?: number;
}

const NO_MODEL = 'ไม่ระบุรุ่น';

export const deviceModelName = (device: DeviceLabelSource): string =>
  device.device_model?.name || device.model || NO_MODEL;

export const maintenanceWarnSuffix = (count?: number | null): string =>
  typeof count === 'number' && count > 0 ? ` ⚠️ มีเคสซ่อมค้าง ${count} เคส` : '';

export function deviceOptionLabel(
  device: DeviceLabelSource,
  options: DeviceLabelOptions = {}
): string {
  const serial = (device.serial_number ?? '').toString().trim();
  const primary =
    (device.code ?? '').toString().trim() ||
    serial ||
    (typeof device.id === 'number' ? `#${device.id}` : 'อุปกรณ์');

  const parts = [primary];
  if (serial) parts.push(`S/N: ${serial}`);
  parts.push(deviceModelName(device));

  let label = parts.join(' - ');
  if (typeof options.availableQuantity === 'number') {
    label += ` (คงเหลือ: ${options.availableQuantity} ${device.unit || 'ชิ้น'})`;
  }
  return label + maintenanceWarnSuffix(options.openMaintenanceCount);
}

/**
 * "ของชิ้นนี้คืออะไร" = ประเภท + ยี่ห้อ + รุ่น — ไม่รวมรหัส
 *
 * feedback ทิวากร (Support) 18 ก.ย. 2026 หน้า "รับเข้าคลัง": ของนับจำนวนที่ไม่มีเลขรุ่น
 * ขึ้นใน dropdown แค่ "DEV178 — คงเหลือ 22 ชุด" จึงไม่รู้ว่ารหัสนี้คือของอะไร · ป้ายเดิมใช้แค่
 * รหัส + รุ่น แต่ของนับจำนวนบน prod ส่วนใหญ่ **ไม่มีรุ่น** และชื่อจริงอยู่ที่ "ประเภท"
 * ("กระดาษ THERMAL 58mm." · "หัวชาร์จ" · "สายชาร์จ") · ส่วนที่มีรุ่นก็ยังซ้ำกันได้ทั้งป้าย —
 * DEV188 (เคส โทรศัพท์ Samsung A07) กับ DEV195 (อุปกรณ์เสริม Samsung A07) เคยขึ้น
 * "· A07 — คงเหลือ 26 ชิ้น" เหมือนกันทุกตัวอักษร
 *
 * รุ่นใช้ `device_model.name` ก่อน แล้วค่อย `model` (ข้อความเดิม) — ลำดับเดียวกับ `deviceModelName`
 * คำที่ซ้ำกัน (ไม่สนตัวพิมพ์) ตัดทิ้ง · ไม่มีอะไรเลย = '' ให้ผู้เรียกตัดสินเองว่าจะเตือนหรือซ่อน
 */
export interface DeviceKindSource {
  device_type?: { name?: string | null } | null;
  brand?: { name?: string | null } | null;
  device_model?: { name?: string | null } | null;
  model?: string | null;
}

export function deviceKindName(device: DeviceKindSource): string {
  const parts = [device.device_type?.name, device.brand?.name, device.device_model?.name || device.model]
    .map((p) => (p ?? '').toString().trim())
    .filter(Boolean);
  const out: string[] = [];
  for (const p of parts) {
    if (!out.some((o) => o.toLowerCase() === p.toLowerCase())) out.push(p);
  }
  return out.join(' ');
}

/**
 * ข้อความยืนยันลบอุปกรณ์ (หน้าข้อมูลอุปกรณ์) — BUG-S2151-002
 * เดิมประกอบเอง `${brand || 'Unknown Brand'} ${model}` → ของไม่มียี่ห้อขึ้น "(Unknown Brand )" ภาษาอังกฤษ
 * และไม่บอกประเภท · ใช้ deviceKindName ตัวเดียวกับทุกจุด · ไม่มีอะไรเลย = ไม่ใส่วงเล็บ
 */
export function deviceDeleteConfirmText(device: DeviceKindSource & { code?: string | null }): string {
  const kind = deviceKindName(device);
  const code = (device.code ?? '').toString().trim() || 'นี้';
  return `คุณต้องการลบอุปกรณ์ ${code}${kind ? ` (${kind})` : ''} ใช่หรือไม่?`;
}

/**
 * ป้ายใน dropdown หน้ารับเข้าคลัง / ปรับปรุงยอด (`CountedDeviceSelect`)
 * - ของนับจำนวน: "DEV178 · กระดาษ THERMAL 58mm. — คงเหลือ 22 ชุด"
 * - ของมี S/N:   "DEV181 · เครื่องหลัก Sunmi V3 — S/N VA08263HJ1149" (ยอดไม่มีความหมายกับของชนิดนี้)
 */
export function stockDeviceOptionLabel(
  device: DeviceKindSource & Pick<DeviceLabelSource, 'code' | 'serial_number' | 'is_serialized' | 'unit'> & {
    quantity?: number | null;
  }
): string {
  const head = [(device.code ?? '').toString().trim(), deviceKindName(device)].filter(Boolean).join(' · ');
  if (device.is_serialized) {
    const serial = (device.serial_number ?? '').toString().trim();
    return serial ? `${head} — S/N ${serial}` : head;
  }
  return `${head} — คงเหลือ ${device.quantity ?? 0} ${device.unit || 'ชิ้น'}`;
}

/** "S/N: xxx" สำหรับบรรทัดรายละเอียด — คืน null เมื่อไม่มี S/N ให้ผู้เรียกซ่อนทั้งส่วน */
export const serialText = (serial?: string | null): string | null => {
  const s = (serial ?? '').toString().trim();
  return s ? `S/N: ${s}` : null;
};
